import { Injectable, NgZone } from '@angular/core';
import { act, Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { ErrorService } from '../../services/error.service';
import { AlertController, ModalController, NavController, ToastController } from '@ionic/angular';
import { PremiumRideService } from 'src/app/services/premium-ride.service';
import { ROUTER_NAVIGATION, RouterNavigatedAction } from '@ngrx/router-store';
import * as fromPremiumRideAction from '../actions/premium-ride.action';
import { catchError, exhaustMap, filter, map, mergeMap, tap } from 'rxjs/operators';
import { APIResponse } from 'src/app/models/api-response.model';
import { failedTransaction, setLoadingSpinner } from '../actions/shared.actions';
import { of } from 'rxjs';
import { getUserOrganizationPoints } from '../actions/organization.actions';
import { reloadUserPass } from '../actions/passes.action';
import { TrackRideComponent } from 'src/app/shared/modals/track-ride/track-ride.component';

@Injectable()
export class PremiumRideEffects {
    
  getPremiumLines$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/ride-plus/premium-lines')
      }),
      mergeMap(() => {
        return this.premiumRideService.getPremiumLines().pipe(
          map((response: APIResponse) => {           
            console.log('getPremiumLines$', response);
            return fromPremiumRideAction.getPremiumLinesSuccess({ data: response.details});
          })
        );
      })
    )
  });

  findRide$ = createEffect(() =>
    {
      return this.actions$.pipe(
        ofType(fromPremiumRideAction.findRide),
        exhaustMap((action) => {
          return this.premiumRideService.findRide(action.data).pipe(
            map((response) => {
              this.alertCtrl.dismiss();
              return fromPremiumRideAction.findRideSuccess({ data: response?.details, selectedRoute: action?.selectedRoute });
            }),
            catchError((response) => {
              console.error('findRide$', response);
              this.store.dispatch(setLoadingSpinner({ status: false }));
              const error = {
                status: response.status,
                error: this.errorService.getErrorMessage(response),
              };
              return of(failedTransaction({ data: error }));
            })
          );
        }
        )
      );
    }
  );

  findRideSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPremiumRideAction.findRideSuccess),
        tap(async (action) => {
          const availableBookings = action.data;
          if(availableBookings.length > 0){
            this.navCtrl.navigateForward('home/ride-plus/available-bookings', {animated: false});
          }
          else{
            this.noRideFound();
          }
        })
      ),
    { dispatch: false }
  );

  bookRide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPremiumRideAction.bookRide),
      exhaustMap((action) =>
        this.premiumRideService.bookRide(action.data).pipe(
          map((response) => {
            this.alertCtrl.dismiss();
            this.bookingAlert(
              'success',
              `Your seat number is ${response.details.reserveSeat}`,
              `Reference Code<br>${response.details.referenceCode}`
            );
            this.store.dispatch(getUserOrganizationPoints());
            this.store.dispatch(reloadUserPass());
            return fromPremiumRideAction.bookRideSuccess({data: response?.details});
          }),
          catchError((response) => {
            console.error('bookRide$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            this.alertCtrl.dismiss();
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  bookRideSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPremiumRideAction.bookRideSuccess),
        tap(async (action) => {
          console.log('bookRideSuccess$', action?.data);
        })
      ),
    { dispatch: false }
  );

  cancelRide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPremiumRideAction.cancelRide),
      exhaustMap((action) =>
        this.premiumRideService.cancelRide(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({status: false}));
            this.modalController.dismiss();
            this.alertCtrl.dismiss();
            this.store.dispatch(getUserOrganizationPoints());
            this.store.dispatch(reloadUserPass());
            return fromPremiumRideAction.cancelRideSuccess({data: response?.details});
          }),
          catchError((response) => {
            console.error('cancelRide$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            this.alertCtrl.dismiss();
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  cancelRideSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPremiumRideAction.cancelRideSuccess),
        tap(async (action) => {
          console.log('cancelRideSuccess$', action?.data);
          this.cancelBookingAlert(
            'success',
            `Booking canceled successfully.`,
            `Your points has been refunded.`
          );
        })
      ),
    { dispatch: false }
  );

  trackRide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPremiumRideAction.trackRide),
      exhaustMap((action) =>
        this.premiumRideService.trackRide(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({status: false}));
            return fromPremiumRideAction.trackRideSuccess({data: response?.details, premiumRouteInfo: action?.data});
          }),
          catchError((response) => {
            console.error('trackRide$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  trackRideSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPremiumRideAction.trackRideSuccess),
        tap(async (action) => {
          const modal = await this.modalController.create({
            component: TrackRideComponent,
            componentProps: {
              rideData: action?.data,
              premiumRouteInfo: action?.premiumRouteInfo
            },
          })
          console.log('trackRideSuccess$', action?.data);
          return modal.present();
        })
      ),
    { dispatch: false }
  );

  waitPremiumRide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPremiumRideAction.waitPremiumRide),
      exhaustMap((action) =>
        this.premiumRideService.waitPremiumRide(action.data).pipe(
          map((response) => {
            this.alertCtrl.dismiss();
            this.bookingAlert(
              'success',
              `Request processed successfully`,
              `You will receive a notification when a seat(s) will be available`
            );
            this.store.dispatch(getUserOrganizationPoints());
            this.store.dispatch(reloadUserPass());
            return fromPremiumRideAction.waitPremiumRideSuccess({data: response?.details});
          }),
          catchError((response) => {
            console.error('bookRide$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            this.alertCtrl.dismiss();
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  waitPremiumRideSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPremiumRideAction.waitPremiumRideSuccess),
        tap(async (action) => {
          console.log('waitPremiumRideSuccess$', action?.data);
        })
      ),
    { dispatch: false }
  );

  findMultipleRides$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPremiumRideAction.findMultipleRides),
      exhaustMap((action) =>
        this.premiumRideService.findMultipleRides(action.data).pipe(
          map((response) => {
            console.log('findMultipleRides$', response);
            this.store.dispatch(setLoadingSpinner({status: false}));
            const details = {
              routeDetails: action?.details,
              payload: action?.data
            }
            return fromPremiumRideAction.findMultipleRidesSuccess({data: response?.details, details: details});
          }),
          catchError((response) => {
            console.error('findMultipleRides$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            this.alertCtrl.dismiss();
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  findMultipleRidesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPremiumRideAction.findMultipleRidesSuccess),
        tap(async (action) => {
          console.log('findMultipleRidesSuccess$', action?.data);
          const availableBookings = action.data;
          if(availableBookings.length > 0){
            this.navCtrl.navigateForward('/home/ride-plus/available-multiple-bookings', {animated: false});
          }
          else{
            this.noRideFound();
          }
        })
      ),
    { dispatch: false }
  );
    
  async presentToast(msg, pos, style) {
    const toast = await this.toastController.create({
    message: msg,
    duration: 3000,
    position: pos,
    cssClass: style
    });
    toast.present();
  }

  async bookingAlert(type, message, submessage){
    const alertImage = (type == 'success') ? '/assets/gifs/booking-success.gif' : '/assets/gifs/error.gif';
    const alertClass = (type == 'success') ? 'booking-success-alert' : 'booking-alert-error';
    const headerTitle = (type == 'success') ? 'Booking Success' : 'Booking Failed';

    const booking = await this.alertCtrl.create({
      cssClass: `booking-response-alert ${alertClass}`,
      header: headerTitle,
      message: `
        <img src="${alertImage}">
        <p id="_booking_reponse_message">${message}</p>
        <p id="_booking_reponse_submessage">${submessage}</p>
      `,
      buttons: [
        {
          text: 'Close',
          role: 'close',
          handler: () => {
            console.log('Closer Alert.');
          }
        }
      ]
    });
    
    await booking.present();

    booking.onDidDismiss().then(() => {
      if (type == 'success') {
        this.navCtrl.navigateBack('/home/bottom-tabs/ride-plus', {animated: false}); 
      }
    });
  }

  async cancelBookingAlert(type, message, submessage) {
    const alertImage = (type == 'success') ? '/assets/gifs/booking-success.gif' : '/assets/gifs/error.gif';
    const alertClass = (type == 'success') ? 'booking-success-alert' : 'booking-alert-error';
    const headerTitle = (type == 'success') ? 'Booking Canceled' : 'Cancel Booking Failed';

    const booking = await this.alertCtrl.create({
      cssClass: `booking-response-alert ${alertClass}`,
      header: headerTitle,
      message: `
        <img src="${alertImage}">
        <p id="_booking_reponse_message">${message}</p>
        <p id="_booking_reponse_submessage">${submessage}</p>
      `,
      buttons: [
        {
          text: 'Close',
          role: 'close',
          handler: () => {
            console.log('Closer Alert.');
          }
        }
      ]
    });
    
    await booking.present();
  }

  async noRideFound() {
    const findRideAlert = await this.alertCtrl.create({
      cssClass: 'no-ride-found-alert',
      header: 'No Ride Found',
      message: `
        <img src="/assets/gifs/error.gif">
        <p id="_find_ride_message">Sorry there are no available rides on the given date and/or route.</p>
        <p id="_find_ride_submessage">Please try again later or select another route & date.</p>
      `,
      buttons: [
        {
          text: 'Close',
          role: 'close',
          handler: () => {
            console.log('No Rides Found.');
          }
        }
      ]
    });

    await findRideAlert.present();
  }

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private errorService: ErrorService,
    private premiumRideService: PremiumRideService,
    private toastController: ToastController,
    private navCtrl: NavController,
    private modalController: ModalController,
    private alertCtrl: AlertController
  ) {}
}

